import React, { useEffect, useState } from 'react';

// 진행 단계 정의 (1~5)
const STAGES = [
    { no: 1, label: '자산등록' },
    { no: 2, label: '리포트작성' },
    { no: 3, label: '결과 업로드' },
    { no: 4, label: '분석' },
    { no: 5, label: '이행진단' },
];

// 슬레이트 블루 계열 팔레트
const C = {
    accent: '#3b5ba5',
    accentDark: '#2f4a86',
    doneBg: '#eef2fb',
    doneText: '#33508f',
    doneBorder: '#c7d4ee',
    todoBg: '#f4f5f7',
    todoText: '#a9afba',
    todoBorder: '#e6e8ec',
    cardBorder: '#e6e8ec',
    headerBg: '#f7f8fa',
    headerText: '#3a3f4b',
    headerSelBg: 'rgb(3, 47, 88)',
    subText: '#8b909b',
    reached: '#3b5ba5',
    notReached: '#d7dbe2',
};

// 자산그룹이 '분석'(4단계) 이상까지 도달했는지 (분석완료 = 4·5단계 포함)
function isAnalysisCompleted(group) {
    return (group.stages && group.stages['4']) || (group.stage || 0) >= 4;
}

// 상단 버튼 스타일
const btnStyle = {
    border: `1px solid ${C.doneBorder}`,
    background: '#fff',
    color: C.accent,
    fontSize: '12px',
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: '6px',
    cursor: 'pointer',
};

// 단계 li 스타일 (번호는 문자열로 표기)
function stageLiStyle(done, current) {
    return {
        padding: '5px 10px',
        borderRadius: '6px',
        border: `1px solid ${current ? C.accentDark : done ? C.doneBorder : C.todoBorder}`,
        backgroundColor: current ? C.accent : done ? C.doneBg : C.todoBg,
        color: current ? '#fff' : done ? C.doneText : C.todoText,
        fontSize: '12px',
        fontWeight: current ? 700 : 500,
        whiteSpace: 'nowrap',
    };
}

// 하나의 자산그룹 진행 라인
function GroupRow({ group }) {
    return (
        <tr>
            <td style={{
                width: '150px', padding: '8px 12px', fontWeight: 600, color: C.headerText,
                borderBottom: '1px solid #f1f2f5', whiteSpace: 'nowrap', fontSize: '13px'
            }}>
                {group.asset_type_nm || group.asset_type}
            </td>
            <td style={{ padding: '8px 12px', borderBottom: '1px solid #f1f2f5' }}>
                <ul style={{
                    display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap',
                    listStyle: 'none', margin: 0, padding: 0,
                }}>
                    {STAGES.map((s) => {
                        const done = (group.stages && group.stages[String(s.no)]) || group.stage >= s.no;
                        const current = group.stage === s.no;
                        return (
                            <li key={s.no} style={stageLiStyle(done, current)}>
                                {`${s.no} ${s.label}`}
                            </li>
                        );
                    })}
                </ul>
            </td>
            <td style={{
                width: '135px', padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap',
                color: C.subText, fontSize: '12px', borderBottom: '1px solid #f1f2f5'
            }}>
                {group.last_activity || '-'}
            </td>
        </tr>
    );
}

/**
 * 회사별 > 자산그룹별 진행 현황 (1~5단계) - 아코디언(다중 펼침)
 * props:
 *   companies       : [{com_cd, com_nm, groups:[{asset_type, asset_type_nm, stage, stages}]}]
 *   isAdmin         : 관리자(ISECURE) 여부
 *   selectedCompany : 현재 선택된 회사코드 (막대그래프 대상)
 *   onSelectCompany : 회사 선택 콜백
 *   chartSlot       : 선택 회사 카드 아래 표시할 막대그래프 노드
 */
function ProgressBoard({ companies = [], isAdmin, selectedCompany, onSelectCompany, chartSlot = null }) {
    // 펼쳐진 회사 집합 (다중 펼침 지원)
    const [openSet, setOpenSet] = useState(() => new Set(selectedCompany ? [selectedCompany] : []));

    // 선택 회사가 정해지면 해당 카드는 펼쳐 둠
    useEffect(() => {
        if (!selectedCompany) return;
        setOpenSet((prev) => {
            const next = new Set(prev);
            next.add(selectedCompany);
            return next;
        });
    }, [selectedCompany]);

    if (!companies || companies.length === 0) {
        return null;
    }

    // 관리자: 헤더 클릭 시 펼치고 '선택'(펼쳐졌지만 선택 안 된 회사도 접지 않고 선택)
    //         이미 펼쳐지고 '선택된' 회사를 다시 클릭할 때만 접기
    // 일반 사용자: 헤더 클릭은 '선택'만(현재 상태 유지)
    const handleHeaderClick = (com) => {
        if (isAdmin) {
            const isOpen = openSet.has(com);
            const isSelected = com === selectedCompany;

            // 이미 펼쳐져 있고 현재 선택된 회사를 다시 클릭 -> 접기
            if (isOpen && isSelected) {
                setOpenSet((prev) => {
                    const next = new Set(prev);
                    next.delete(com);
                    return next;
                });
                return;
            }

            // 그 외(닫혀 있거나, 펼쳐졌지만 선택되지 않음) -> 펼치고 선택(접지 않음)
            setOpenSet((prev) => {
                const next = new Set(prev);
                next.add(com);
                return next;
            });
            if (onSelectCompany) onSelectCompany(com);
        } else if (onSelectCompany) {
            onSelectCompany(com);
        }
    };

    const expandAll = () => setOpenSet(new Set(companies.map((c) => c.com_cd)));
    const collapseAll = () => setOpenSet(new Set());

    return (
        <div className="section">
            <div className="sub_title" style={{
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px',
                height: 'auto', minHeight: '34px', color: C.headerText,
            }}>
                <span>{'■ 진행 현황 (회사별 · 자산그룹별)'}</span>
                {isAdmin && (
                    <span style={{ fontWeight: 700, fontSize: '12px', color: C.accent }}>
                        {`총 ${companies.length}개사`}
                    </span>
                )}
                <span style={{ fontWeight: 400, fontSize: '12px', color: C.subText }}>
                    {isAdmin
                        ? '최근 3개월 내 자산등록/취약점 리포트작성 회사'
                        : '자산그룹별 진행 단계'}
                </span>
                {isAdmin && (
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                        <button type="button" style={btnStyle} onClick={expandAll}>모두 펼침</button>
                        <button type="button" style={btnStyle} onClick={collapseAll}>모두 접기</button>
                    </span>
                )}
            </div>
            <div style={{ padding: '6px 10px 12px' }}>
                {companies.map((company) => {
                    const isOpen = openSet.has(company.com_cd);
                    const isSelected = company.com_cd === selectedCompany;
                    const completedCount = (company.groups || []).filter(isAnalysisCompleted).length;
                    return (
                        <React.Fragment key={company.com_cd}>
                            <div
                                style={{
                                    border: `1px solid ${isSelected ? C.headerSelBg : C.cardBorder}`,
                                    borderRadius: '10px',
                                    marginBottom: '9px',
                                    overflow: 'hidden',
                                    boxShadow: isSelected ? '0 1px 6px rgba(3,47,88,0.15)' : 'none',
                                    transition: 'border-color .15s ease, box-shadow .15s ease',
                                }}
                            >
                                <div
                                    onClick={() => handleHeaderClick(company.com_cd)}
                                    style={{
                                        padding: '11px 14px',
                                        backgroundColor: isSelected ? C.headerSelBg : C.headerBg,
                                        color: isSelected ? '#fff' : C.headerText,
                                        fontWeight: 700,
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        userSelect: 'none',
                                    }}
                                >
                                    <span style={{
                                        display: 'inline-block',
                                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                        transition: 'transform .15s ease',
                                        fontSize: '12px',
                                        color: isSelected ? '#ffffff' : C.subText,
                                    }}>▶</span>
                                    <span>{company.com_nm || company.com_cd}</span>
                                    <span style={{
                                        fontWeight: 400, fontSize: '12px',
                                        color: isSelected ? '#ffffff' : C.subText,
                                    }}>
                                        ({company.com_cd})
                                    </span>
                                    <span style={{
                                        marginLeft: 'auto',
                                        fontWeight: 600, fontSize: '12px',
                                        padding: '2px 10px',
                                        borderRadius: '11px',
                                        backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : C.doneBg,
                                        color: isSelected ? '#fff' : C.doneText,
                                    }}>
                                        {`자산그룹 ${company.groups ? company.groups.length : 0} · 분석완료 ${completedCount}`}
                                    </span>
                                </div>
                                {isOpen && (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff' }}>
                                        <tbody>
                                            {company.groups && company.groups.length > 0 ? (
                                                company.groups.map((g) => (
                                                    <GroupRow key={g.asset_type} group={g} />
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={3} style={{ padding: '10px 12px', color: C.subText }}>
                                                        등록된 자산그룹이 없습니다.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                            {isOpen && isSelected && chartSlot}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}

export default ProgressBoard;
